import React, { useCallback, useMemo, useContext, useState } from 'react';
import { makeStyles, TextField, Dropdown, DropdownItem } from '@ellucian/react-design-system/core';
import { parsePhoneNumber } from 'react-phone-number-input';
import { getCountryCallingCode } from 'react-phone-number-input/input';
import 'react-phone-number-input/style.css';
import flags from 'react-phone-number-input/flags';
import { sizingXxLarge, spacing30 } from '@ellucian/react-design-system/core/styles/tokens';
import { useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import get from 'lodash/get';
import head from 'lodash/head';
import identity from 'lodash/identity';
import toPath from 'lodash/toPath';
import PhoneInput from 'react-phone-number-input';
import phoneMetadata from 'libphonenumber-js/metadata.min.json';
import phoneLabels from 'react-phone-number-input/locale/en.json';

const PhoneContext = React.createContext({ countryChanged: false, setCountryChanged: () => { } });

export const getPhoneDisplayValue = (fullValue) => {
    if (!fullValue) return '';
    try {
        const phoneNumber = parsePhoneNumber(fullValue);
        if (phoneNumber) {
            return phoneNumber.nationalNumber || phoneNumber.formatNational();
        }
    } catch (_e) {
        // ignore
    }
    return fullValue;
};

const useStyles = makeStyles({
    name: 'PhoneNumber'
})((theme) => ({
    phoneInput: {
        [theme.breakpoints.up('md')]: {
            display: 'flex !important',
            alignItems: 'start !important',
            gap: spacing30,
        },
        [theme.breakpoints.down('md')]: {
            display: 'block !important',
            width: '100%',
        },
    },
    dropdownIcon: {
        right: `${spacing30} !important`,
    },
    selectMenu: {
        minHeight: ['1.25rem', '!important'],
        minWidth: sizingXxLarge,
        [theme.breakpoints.down('md')]: {
            minWidth: 'unset',
            width: '100%',
        },
    },
    dropdownWrapper: {
        flex: '0 1 auto',
        '& .MuiOutlinedInput-root': {
            minWidth: 'unset',
        },
        '& .MuiSelect-select': {
            minWidth: '0 !important',
            width: 'auto',
        },
        '& .MuiFormControl-root': {
            width: 'auto',
        },
        [theme.breakpoints.down('md')]: {
            marginBottom: spacing30,
        },
    },
}));

function getSelectedOption(options, value) {
    for (const option of options) {
        if (option.value === value) {
            return option;
        }
    }
}

const DropdownWrapper = (props) => {
    const { classes } = useStyles();
    const {
        value,
        onChange,
        options,
        iconComponent: Icon,  
        ...rest
    } = props;
    const onChangeCallback = useCallback((event) => {
        const val = event.target.value;
        onChange(val);
    }, [onChange]);

    const selectedOption = useMemo(() => {
        return getSelectedOption(options, value);
    }, [options, value]);

    const countryCallingCodeValue = useMemo(() => {
        if (selectedOption && selectedOption.value) {
            try {
                return getCountryCallingCode(selectedOption.value);
            } catch (_e) {
                return '';
            }
        }
        return '';
    }, [selectedOption]);

    return (
        <div className={classes.dropdownWrapper}>
            <Dropdown
                classes={{ icon: classes.dropdownIcon, select: classes.selectMenu }}
                {...rest}
                value={value}
                onChange={onChangeCallback}
                renderValue={() => (
                    <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                        {selectedOption && selectedOption.value && <Icon country={selectedOption.value} />}
                        {countryCallingCodeValue && (
                            <span style={{ marginLeft: '8px' }}>
                                +{countryCallingCodeValue}
                            </span>
                        )}
                    </div>
                )}
            >
                {options.map((country) => (
                    country.value &&
                    <DropdownItem
                        key={country.value} // Changed from country.label
                        value={country.value} // Changed from country.value || country.label
                        LeftIconComponent={
                            <Icon
                                country={country.value}
                                label={country.label && country.label}
                            />
                        }
                    >
                        {country.label}
                    </DropdownItem>
                ))}
            </Dropdown>
        </div>
    );
};

const TextFieldWrapper = React.memo(React.forwardRef((props, ref) => {
    const {
        helperText: helperTextProp,
        onFocus: onFocusProp,
        required,
        value: fullValue,
        ...rest
    } = props;
    const { name } = useContext(PhoneContext);
    
    const displayValue = useMemo(() => {
        return getPhoneDisplayValue(fullValue);
    }, [fullValue]);
    
    // Memoize selectors to prevent recreation on every render
    const selectAlwaysShowError = useMemo(() => {
        return createSelector(
            state => get(state.form.meta, `forceShowErrors.${head(toPath(name))}`),
            value => !!value
        );
    }, [name]);
    
    const selectError = useMemo(() => {
        return createSelector(
            state => get(state.form.errors, toPath(name)),
            identity
        );
    }, [name]);
    
    const selectTouched = useMemo(() => {
        return createSelector(
            state => get(state.form.touched, toPath(name)),
            identity
        );
    }, [name]);
    const onFocus = useCallback((...args) => {
        if (ref && ref.current) {
            ref.current.selectionStart = ref.current.value.length;
            ref.current.selectionEnd = ref.current.value.length;
        }
        onFocusProp(...args);
    }, [ref, ref?.current, onFocusProp]);

    const error = useSelector(selectError);
    const touched = useSelector(selectTouched);
    const alwaysShowError = useSelector(selectAlwaysShowError);
    let hasError = false;
    let helperText = helperTextProp;
    if (error && (touched || alwaysShowError)) {
        hasError = true;
        helperText = error;
    }
    return (
        <TextField
            {...rest}
            error={hasError}
            fullWidth
            helperText={helperText}
            inputProps={{ ref }}
            name={name}
            onFocus={onFocus}
            required={required}
            value={displayValue}
        />
    );
}));
TextFieldWrapper.displayName = 'TextFieldWrapper';

const PhoneNumber = React.memo(props => {
    const { classes } = useStyles();
    const { value, onBlur, name: nameProp, onFocus, onChange, required, countryLabel = '', country, ...rest } = props; // Added country prop
    const [countryChanged, setCountryChanged] = useState(false);
    const ctx = useMemo(() => ({
        countryChanged,
        name: nameProp,
        setCountryChanged,
    }), [nameProp, countryChanged]);
    return (
        <PhoneContext.Provider value={ctx}>
            <PhoneInput
                flags={flags}
                metadata={phoneMetadata}
                labels={phoneLabels}
                value={value}
                onChange={onChange}
                inputComponent={TextFieldWrapper}
                required={required}
                country={country} // Pass country to PhoneInput
                {...rest}
                onBlur={onBlur}
                onFocus={onFocus}
                countrySelectComponent={DropdownWrapper}
                countrySelectProps={{ label: countryLabel }}
                className={classes.phoneInput}
                onCountryChange={() => setCountryChanged(true)}
            />
        </PhoneContext.Provider>
    );
});

PhoneNumber.displayName = 'PhoneNumber';
export default PhoneNumber;
